import * as THREE from 'three';
import { AssetManager } from './AssetManager';

export class DayNightShader {
  private visualHour = 12.0;
  private readonly cycleDurationMs = 15000; // 15 seconds full cycle

  // Lights reference
  private ambientLight: THREE.AmbientLight;
  private sunLight: THREE.DirectionalLight;

  constructor(ambientLight: THREE.AmbientLight, sunLight: THREE.DirectionalLight) {
    this.ambientLight = ambientLight;
    this.sunLight = sunLight;
  }

  public update(_time: number, delta: number, simSpeed: number) {
    // Progress hour cycle based on simulation speed (0 means paused, fallback to 1x speed for camera panning)
    const speed = simSpeed === 0 ? 1.0 : 1.0 * simSpeed;
    this.visualHour += (delta / (this.cycleDurationMs * 0.001)) * (speed * 0.04); // subtle hour clock tick

    if (this.visualHour >= 24.0) {
      this.visualHour = 0.0;
    }

    this.applyLightTransitions();
  }

  public getFormattedTime(): string {
    const hours = Math.floor(this.visualHour);
    const minutes = Math.floor((this.visualHour - hours) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  private applyLightTransitions() {
    const hour = this.visualHour;

    // Orbit the sun in a circular arc above the centered grid (center is around 0, 0, 0)
    const angle = ((hour - 6.0) / 24.0) * Math.PI * 2; // Sunrise starts around 6:00
    const radius = 22;
    
    // Position sun light
    this.sunLight.position.set(
      Math.cos(angle) * radius,
      Math.max(2.0, Math.sin(angle) * radius), // don't go below ground level
      Math.cos(angle) * 0.4 * radius
    );

    let sunColor = new THREE.Color(0xfff9c4);
    let sunIntensity = 1.3;
    let ambientColor = new THREE.Color(0xe0f2f1);
    let ambientIntensity = 0.65;
    let glowIntensity = 0.0;

    if (hour >= 19.0 || hour < 5.0) {
      // --- NIGHT (19:00 to 5:00) ---
      sunColor.setHex(0x1a237e); // Dim blue moon
      sunIntensity = 0.22;
      ambientColor.setHex(0x0a0e29); // Dark blue sky
      ambientIntensity = 0.28;
      glowIntensity = 1.3; // Windows glow brightly
    } 
    else if (hour >= 5.0 && hour < 8.0) {
      // --- DAWN/MORNING (5:00 to 8:00) ---
      const p = (hour - 5.0) / 3.0; // 0 to 1
      
      // Interpolate colors and intensities
      sunColor.lerpColors(new THREE.Color(0x1a237e), new THREE.Color(0xffb74d), p); // blue moon to rose-gold
      sunIntensity = THREE.MathUtils.lerp(0.22, 0.95, p);

      ambientColor.lerpColors(new THREE.Color(0x0a0e29), new THREE.Color(0xb2dfdb), p); // dark indigo to light cyan
      ambientIntensity = THREE.MathUtils.lerp(0.28, 0.55, p);

      glowIntensity = THREE.MathUtils.lerp(1.3, 0.0, p); // windows slowly fade out
    } 
    else if (hour >= 8.0 && hour < 16.0) {
      // --- MIDDAY (8:00 to 16:00) ---
      const p = (hour - 8.0) / 8.0;
      
      sunColor.lerpColors(new THREE.Color(0xffb74d), new THREE.Color(0xfff9c4), p); // morning to noon white
      sunIntensity = THREE.MathUtils.lerp(0.95, 1.35, p);

      ambientColor.lerpColors(new THREE.Color(0xb2dfdb), new THREE.Color(0xe0f2f1), p);
      ambientIntensity = THREE.MathUtils.lerp(0.55, 0.65, p);

      glowIntensity = 0.0;
    } 
    else {
      // --- DUSK/SUNSET (16:00 to 19:00) ---
      const p = (hour - 16.0) / 3.0;

      sunColor.lerpColors(new THREE.Color(0xfff9c4), new THREE.Color(0xff7043), p); // noon to fiery orange
      sunIntensity = THREE.MathUtils.lerp(1.35, 0.8, p);

      ambientColor.lerpColors(new THREE.Color(0xe0f2f1), new THREE.Color(0x7e57c2), p); // clear blue to dusky violet
      ambientIntensity = THREE.MathUtils.lerp(0.65, 0.45, p);

      glowIntensity = THREE.MathUtils.lerp(0.0, 1.3, p); // windows slowly glow yellow
    }

    this.sunLight.color.copy(sunColor);
    this.sunLight.intensity = sunIntensity;

    this.ambientLight.color.copy(ambientColor);
    this.ambientLight.intensity = ambientIntensity;

    // Apply nocturnal emissive window glows
    AssetManager.getInstance().setNocturnalGlow(glowIntensity);
  }
}
